import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';

// RED ATTACK THEME - Colors
const ATTACK_COLORS = {
  primary: '#f44336',      // Rouge principal
  primaryDark: '#d32f2f',  // Dark red
  primaryLight: '#e57373', // Rouge clair
  success: '#e53935',      // Bright red for success
  warning: '#ff9800',      // Orange pour warning
  danger: '#b71c1c',       // Very dark red
  accent: '#ff5252',       // Accent rouge
  background: '#1a0000',   // Fond noir-rouge
  surface: '#2d0000',      // Surface
  text: '#ffffff',         // Texte blanc
  textSecondary: '#ffcdd2', // Texte secondaire rouge clair
};

// Custom node component for the attack graph - RED THEME
const AttackNode = ({ data }) => {
  const getNodeColor = () => {
    if (data.status === 'completed') return ATTACK_COLORS.success;
    if (data.status === 'current') return ATTACK_COLORS.warning;
    if (data.status === 'failed') return ATTACK_COLORS.danger;
    return '#757575';
  };

  const getNodeIcon = () => {
    switch (data.type) {
      case 'target': return '🎯';
      case 'vulnerability': return '🔓';
      case 'exploit': return '💥';
      case 'access': return '🖥️';
      case 'data': return '💾';
      default: return '🔗';
    }
  };

  return (
    <div
      style={{
        minWidth: 220,
        border: `3px solid ${getNodeColor()}`,
        backgroundColor: data.status === 'current' ? 'rgba(244, 67, 54, 0.15)' : '#1e1e1e',
        borderRadius: '12px',
        padding: '16px',
        cursor: 'pointer',
        transition: 'all 0.3s',
        boxShadow: data.status === 'current'
          ? `0 0 20px ${ATTACK_COLORS.warning}`
          : `0 4px 12px rgba(244, 67, 54, 0.3)`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', minWidth: 0 }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: getNodeColor(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          fontSize: '18px',
          flexShrink: 0,
        }}>
          {getNodeIcon()}
        </div>
        <span style={{ fontWeight: 'bold', color: '#fff', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.label}>
          {data.label}
        </span>
      </div>

      {data.probability && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', color: '#aaa' }}>Probability</span>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: ATTACK_COLORS.primary }}>
              {data.probability}%
            </span>
          </div>
          <div style={{
            height: 6,
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: 3,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${data.probability}%`,
              height: '100%',
              backgroundColor: data.probability > 70 ? ATTACK_COLORS.success : data.probability > 40 ? ATTACK_COLORS.warning : ATTACK_COLORS.danger,
              borderRadius: 3,
              transition: 'width 0.5s ease',
            }} />
          </div>
        </div>
      )}

      {data.technique && (
        <span style={{
          display: 'inline-block',
          padding: '4px 8px',
          backgroundColor: 'rgba(244, 67, 54, 0.2)',
          border: `1px solid ${ATTACK_COLORS.primary}`,
          borderRadius: '4px',
          fontSize: '11px',
          color: ATTACK_COLORS.primaryLight,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }} title={data.technique}>
          {data.technique}
        </span>
      )}
    </div>
  );
};

const nodeTypes = {
  attackNode: AttackNode,
};

const VisualAttackInterface = ({ scanId, scanResults }) => {
  // Main states
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [attackMode, setAttackMode] = useState('simulation');
  const [isRunning, setIsRunning] = useState(false);

  // AI Coach states
  const [aiExplanation, setAiExplanation] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiThinking, setAiThinking] = useState(false);

  // Wizard states
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeWizard, setActiveWizard] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);

  // Console states
  const [terminalOutput, setTerminalOutput] = useState([]);
  const [terminalExpanded, setTerminalExpanded] = useState(true);

  // Statistics states
  const [attackStats, setAttackStats] = useState({
    currentPhase: 'Reconnaissance',
    successRate: 0,
    timeElapsed: '00:00',
    toolsUsed: 0,
    vulnerabilitiesFound: 0,
  });

  const terminalRef = useRef(null);

  // Initialiser le graphe d'attaque
  useEffect(() => {
    generateAttackGraph();
  }, [scanResults]);

  const generateAttackGraph = async () => {
    addToTerminal('🤖 AI analyzing scan results...', 'info');
    setAiThinking(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    // Create the attack graph
    const initialNodes = [
      {
        id: '1',
        type: 'attackNode',
        position: { x: 250, y: 0 },
        data: {
          label: 'Cible',
          type: 'target',
          status: 'completed',
          description: scanResults?.target || '192.168.1.100',
        },
      },
      {
        id: '2',
        type: 'attackNode',
        position: { x: 100, y: 150 },
        data: {
          label: 'SQL Injection',
          type: 'vulnerability',
          status: 'current',
          probability: 95,
          technique: 'T1190',
          description: 'SQLi vulnerability detected on /login.php',
        },
      },
      {
        id: '3',
        type: 'attackNode',
        position: { x: 400, y: 150 },
        data: {
          label: 'RCE Struts',
          type: 'vulnerability',
          status: 'pending',
          probability: 85,
          technique: 'T1190',
          description: 'Apache Struts CVE-2017-5638',
        },
      },
      {
        id: '4',
        type: 'attackNode',
        position: { x: 100, y: 300 },
        data: {
          label: 'Shell www-data',
          type: 'access',
          status: 'pending',
          probability: 90,
          technique: 'T1059',
          description: 'Shell via SQLi to RCE',
        },
      },
      {
        id: '5',
        type: 'attackNode',
        position: { x: 400, y: 300 },
        data: {
          label: 'Shell direct',
          type: 'access',
          status: 'pending',
          probability: 80,
          technique: 'T1059',
          description: 'Shell via Struts exploit',
        },
      },
      {
        id: '6',
        type: 'attackNode',
        position: { x: 250, y: 450 },
        data: {
          label: 'Escalade Root',
          type: 'exploit',
          status: 'pending',
          probability: 75,
          technique: 'T1068',
          description: 'Kernel exploit ou SUID',
        },
      },
      {
        id: '7',
        type: 'attackNode',
        position: { x: 250, y: 600 },
        data: {
          label: 'Database',
          type: 'data',
          status: 'pending',
          probability: 95,
          technique: 'T1005',
          description: '50K+ clients PII',
        },
      },
    ];

    const initialEdges = [
      { id: 'e1-2', source: '1', target: '2', animated: true, style: { stroke: ATTACK_COLORS.primary }, markerEnd: { type: MarkerType.ArrowClosed, color: ATTACK_COLORS.primary } },
      { id: 'e1-3', source: '1', target: '3', animated: true, style: { stroke: ATTACK_COLORS.primary }, markerEnd: { type: MarkerType.ArrowClosed, color: ATTACK_COLORS.primary } },
      { id: 'e2-4', source: '2', target: '4', animated: true, style: { stroke: ATTACK_COLORS.primary }, markerEnd: { type: MarkerType.ArrowClosed, color: ATTACK_COLORS.primary } },
      { id: 'e3-5', source: '3', target: '5', animated: true, style: { stroke: ATTACK_COLORS.primary }, markerEnd: { type: MarkerType.ArrowClosed, color: ATTACK_COLORS.primary } },
      { id: 'e4-6', source: '4', target: '6', animated: true, style: { stroke: ATTACK_COLORS.primary }, markerEnd: { type: MarkerType.ArrowClosed, color: ATTACK_COLORS.primary } },
      { id: 'e5-6', source: '5', target: '6', animated: true, style: { stroke: ATTACK_COLORS.primary }, markerEnd: { type: MarkerType.ArrowClosed, color: ATTACK_COLORS.primary } },
      { id: 'e6-7', source: '6', target: '7', animated: true, style: { stroke: ATTACK_COLORS.primary }, markerEnd: { type: MarkerType.ArrowClosed, color: ATTACK_COLORS.primary } },
    ];

    setNodes(initialNodes);
    setEdges(initialEdges);
    setAiThinking(false);

    // Explication IA initiale
    const explanation = `🎯 **Strategic Analysis Completed**

I identified **2 promising attack paths** toward your objective (database access):

**🥇 Optimal Path (95% Probability):**
1. SQL Injection → Shell www-data → Root Escalation → Database

**🥈 Alternative Path (85% Probability):**
2. RCE Apache Struts → Direct shell → Root Escalation → Database

**💡 Recommendation:**
I suggest starting with **path 1** because:
- ✅ Higher probability of success (95%)
- ✅ SQLi already confirmed on /login.php
- ✅ Less noisy exploitation (less detection)

**Click a node** to see details and launch the AI-assisted wizard.`;

    setAiExplanation(explanation);
    addToTerminal('✅ Attack graph generated successfully', 'success');
  };

  const addToTerminal = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setTerminalOutput(prev => [
      ...prev,
      { timestamp, message, type },
    ]);

    if (terminalRef.current) {
      setTimeout(() => {
        terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
      }, 100);
    }
  };

  const onNodeClick = useCallback(async (event, node) => {
    setSelectedNode(node);
    setAiThinking(true);

    addToTerminal(`🎯 Selected: ${node.data.label}`, 'info');

    await generateNodeExplanation(node);

    setAiThinking(false);
  }, []);

  const generateNodeExplanation = async (node) => {
    await new Promise(resolve => setTimeout(resolve, 1500));

    const explanations = {
      '2': `🔍 **SQL Injection - Detailed Analysis**

**Detected Vulnerability:**
- URL: http://192.168.1.100/login.php?id=1
- Type: Blind Boolean-based SQLi
- Vulnerable parameter: \`id\`

**Success Probability: 95%** ✅

**Why this probability?**
- ✅ Injection confirmed by automated tests
- ✅ Database: MySQL 5.7 (vulnerable)
- ✅ No WAF detected
- ⚠️ Risque: Slow exploitation (blind)

**Next Steps:**
1. **Wizard SQLMap** → Configuration automatique
2. **Simulation** → Test without real impact
3. **Real exploitation** → Data extraction

**Impact Attendu:**
- 🎯 Shell www-data (user web)
- 📊 Full database access
- ⏱️ Estimated time: 5-10 minutes

**Cliquez sur "Lancer Wizard" pour commencer!**`,

      '3': `🔥 **RCE Apache Struts - Detailed Analysis**

**Detected Vulnerability:**
- CVE: CVE-2017-5638
- Service: Apache Tomcat 9.0 + Struts 2
- Port: 8080

**Success Probability: 85%** ⚠️

**Why this probability?**
- ✅ Version confirmed vulnerable
- ✅ Exploit public disponible (Metasploit)
- ⚠️ May be unstable (crash possible)
- ⚠️ More detectable by IDS/IPS

**Avantages:**
- 🚀 VERY fast exploitation (< 1 minute)
- 🎯 Direct shell (often with more privileges)
- 💪 RCE complet (upload fichiers, etc.)

**Drawbacks:**
- 🔊 Technique bruyante (logs applicatifs)
- 💥 Risque de crash du service
- 🚨 EDR detection more likely

**Recommandation IA:**
Use if SQLi fails or takes too long.`,

      '6': `⚡ **Privilege Escalation - Strategy**

**Objectif:** Obtenir root/SYSTEM

**Success Probability: 75%** ⚠️

**Techniques Disponibles:**

**🥇 Kernel Exploit (if version vulnerable)**
- Exploit: DirtyCow, PwnKit, ou autre selon kernel
- Probability: 60%
- Risk: System crash possible

**🥈 SUID Binaries**
- Chercher: find / -perm -4000
- Probability: 40%
- Risque: Faible

**🥉 Sudo Misconfiguration**
- Test: sudo -l
- Probability: 30%
- Risk: Very low

**💡 Recommended Strategy:**
1. Automated enumeration (LinPEAS/WinPEAS)
2. AI analyzes results
3. Selection of best vector
4. Guided exploitation

**Note:** AI will automatically detect the best path after enumeration.`,

      '7': `🎯 **Final Objective - Database**

**Cible:** MySQL Database (shop_db)

**Success Probability: 95%** ✅

**Estimated Data:**
- 📊 ~50,000 clients (PII)
- 💳 Financial transactions
- 🔑 Credentials admin

**Access Scenario:**

**Si Root obtenu:**
- ✅ Direct access to MySQL files
- ✅ Dump complet possible
- ✅ Pas de restrictions

**Si www-data uniquement:**
- ⚠️ Via application web
- ⚠️ Limited permissions
- ✅ Toujours possible via SQLi

**Plan d'Exfiltration (selon RoE):**
1. 🔢 COUNT(*) → Quantify the data
2. 🔐 Hash du dataset complet
3. 📄 Sample de 5-10 enregistrements (PII redacted)
4. ❌ PAS de dump complet (compliance)

**Impact Business:**
- 🚨 Violation RGPD critique
- 💰 Fine: up to €20M
- 📢 Notification CNIL obligatoire

**Ready for extraction?**`,
    };

    setAiExplanation(explanations[node.id] || 'Analyse en cours...');

    // Generate suggestions
    const suggestions = {
      '2': [
        {
          title: 'Lancer Wizard SQLMap',
          icon: '🧙',
          action: 'wizard_sqlmap',
          recommended: true,
        },
        {
          title: 'Test Manuel SQLi',
          icon: '🖥️',
          action: 'manual_sqli',
          recommended: false,
        },
        {
          title: 'Full Simulation',
          icon: '🧪',
          action: 'simulate',
          recommended: true,
        },
      ],
      '3': [
        {
          title: 'Wizard Metasploit Struts',
          icon: '🧙',
          action: 'wizard_struts',
          recommended: true,
        },
        {
          title: 'Check Version',
          icon: 'ℹ️',
          action: 'version_check',
          recommended: false,
        },
      ],
    };

    setAiSuggestions(suggestions[node.id] || []);
  };

  const handleSuggestionClick = (action) => {
    if (action.startsWith('wizard_')) {
      const wizardType = action.replace('wizard_', '');
      setActiveWizard(wizardType);
      setWizardStep(0);
      setWizardOpen(true);
    } else if (action === 'simulate') {
      startSimulation();
    }
  };

  const startSimulation = async () => {
    addToTerminal('🧪 Lancement de la simulation...', 'info');
    setIsRunning(true);
    setAttackMode('simulation');

    for (let i = 0; i < 5; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      addToTerminal(`[SIM] Step ${i + 1}/5 - Test SQLi payload #${i + 1}`, 'info');
    }

    addToTerminal('✅ Simulation successful! Exploitation confirmed as viable.', 'success');
    setIsRunning(false);

    setAiExplanation(`✅ **Simulation Completed**

**Result:** Exploitation confirmed as **viable and safe**.

**Tests Performed:**
- ✅ 5 SQLi payloads tested
- ✅ No crash detected
- ✅ Extraction confirmed possible
- ✅ Estimated time: 8 minutes

**You can now:**
1. 🚀 Launch the REAL attack
2. 📊 View the detailed simulation report
3. 🔄 Change technique

**Ready to switch to LIVE mode?**`);
  };

  const startLiveAttack = () => {
    setAttackMode('live');
    addToTerminal('🚀 SWITCHING TO LIVE MODE - Real attack launched', 'warning');
  };

  // Rendu du Wizard
  const renderWizard = () => {
    if (!activeWizard || !wizardOpen) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: '#1e1e1e',
          borderRadius: '16px',
          padding: '24px',
          width: '600px',
          maxHeight: '80vh',
          overflow: 'auto',
          border: `2px solid ${ATTACK_COLORS.primary}`,
          boxShadow: `0 0 40px ${ATTACK_COLORS.primary}40`,
        }}>
          <h2 style={{ color: ATTACK_COLORS.primary, marginBottom: '20px' }}>
            🧙‍♂️ Wizard {activeWizard.toUpperCase()} - AI-Assisted
          </h2>

          <div style={{
            padding: '16px',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            borderRadius: '8px',
            marginBottom: '20px',
            border: `1px solid ${ATTACK_COLORS.primary}40`,
          }}>
            <p style={{ color: ATTACK_COLORS.textSecondary, margin: 0 }}>
              🤖 AI has pre-configured the optimal parameters for this attack.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: '#fff', display: 'block', marginBottom: '8px' }}>URL Cible</label>
            <input
              type="text"
              defaultValue="http://192.168.1.100/login.php?id=1"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#2d2d2d',
                border: `1px solid ${ATTACK_COLORS.primary}`,
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
              }}
            />
          </div>

          <div style={{
            backgroundColor: '#0d1117',
            padding: '16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            color: ATTACK_COLORS.primary,
            marginBottom: '20px',
          }}>
            sqlmap -u "http://192.168.1.100/login.php?id=1" --batch --dump --tamper=space2comment
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setWizardOpen(false)}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Annuler
            </button>
            <button
              onClick={() => {
                startSimulation();
                setWizardOpen(false);
              }}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              🧪 Simulation
            </button>
            <button
              onClick={() => {
                startLiveAttack();
                setWizardOpen(false);
              }}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: ATTACK_COLORS.primary,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              🚀 Attaque LIVE
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#0d0d0d',
    }}>
      {/* Header - RED THEME */}
      <div style={{
        padding: '16px 24px',
        backgroundColor: '#1a0000',
        borderBottom: `2px solid ${ATTACK_COLORS.primary}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <h1 style={{
            color: ATTACK_COLORS.primary,
            margin: 0,
            fontSize: '24px',
            textShadow: `0 0 10px ${ATTACK_COLORS.primary}`,
          }}>
            💀 ATTACK! - Interface Visuelle
          </h1>
          <p style={{ color: ATTACK_COLORS.textSecondary, margin: '4px 0 0', fontSize: '14px' }}>
            Real-time AI guidance • Intelligent wizards • Simulation before attack
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <span style={{
            padding: '8px 16px',
            backgroundColor: attackMode === 'simulation' ? '#2196f3' : ATTACK_COLORS.primary,
            color: '#fff',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 'bold',
          }}>
            {attackMode === 'simulation' ? '🧪 SIMULATION' : '🚀 LIVE'}
          </span>
          <span style={{
            padding: '8px 16px',
            backgroundColor: ATTACK_COLORS.primary,
            color: '#fff',
            borderRadius: '20px',
            fontSize: '12px',
          }}>
            Phase: {attackStats.currentPhase}
          </span>
          <span style={{
            padding: '8px 16px',
            backgroundColor: '#333',
            color: '#fff',
            borderRadius: '20px',
            fontSize: '12px',
            border: `1px solid ${ATTACK_COLORS.primary}`,
          }}>
            ⏱️ {attackStats.timeElapsed}
          </span>
        </div>
      </div>

      {/* Contenu principal */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Colonne gauche - Graphe d'attaque */}
        <div style={{ flex: 2, display: 'flex', flexDirection: 'column' }}>
          <div style={{
            margin: '8px',
            padding: '12px',
            backgroundColor: '#1e1e1e',
            borderRadius: '8px',
            border: `1px solid ${ATTACK_COLORS.primary}30`,
          }}>
            <h3 style={{ color: ATTACK_COLORS.primary, margin: '0 0 4px' }}>
              📊 Graphe d'Attaque Interactif
            </h3>
            <p style={{ color: '#888', margin: 0, fontSize: '13px' }}>
              Click a node to see details and attack options
            </p>
          </div>

          <div style={{
            margin: '8px',
            flex: 1,
            backgroundColor: '#0d0d0d',
            borderRadius: '12px',
            border: `1px solid ${ATTACK_COLORS.primary}30`,
            overflow: 'hidden',
          }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              nodeTypes={nodeTypes}
              fitView
              style={{ backgroundColor: '#0d0d0d' }}
            >
              <Background color={ATTACK_COLORS.primary} gap={20} size={1} style={{ opacity: 0.1 }} />
              <Controls style={{ backgroundColor: '#1e1e1e', borderRadius: '8px' }} />
              <MiniMap
                nodeColor={ATTACK_COLORS.primary}
                maskColor="rgba(0, 0, 0, 0.8)"
                style={{ backgroundColor: '#1e1e1e', borderRadius: '8px' }}
              />
            </ReactFlow>
          </div>
        </div>

        {/* Colonne droite - IA Coach + Console */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* IA Coach */}
          <div style={{
            margin: '8px',
            padding: '16px',
            backgroundColor: '#1e1e1e',
            borderRadius: '12px',
            border: `1px solid ${ATTACK_COLORS.primary}30`,
            maxHeight: '50%',
            overflow: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: ATTACK_COLORS.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                fontSize: '20px',
              }}>
                🤖
              </div>
              <h3 style={{ color: '#fff', margin: 0 }}>Coach IA en Direct</h3>
              {aiThinking && (
                <span style={{
                  marginLeft: '12px',
                  color: ATTACK_COLORS.primary,
                  animation: 'pulse 1s infinite',
                }}>
                  ⏳ Analyse...
                </span>
              )}
            </div>

            <div style={{
              color: '#ddd',
              fontSize: '13px',
              lineHeight: '1.6',
              whiteSpace: 'pre-line',
            }}>
              {aiExplanation}
            </div>

            {aiSuggestions.length > 0 && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${ATTACK_COLORS.primary}30` }}>
                <h4 style={{ color: ATTACK_COLORS.primary, margin: '0 0 12px' }}>💡 Suggested Actions</h4>
                {aiSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion.action)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      marginBottom: '8px',
                      backgroundColor: suggestion.recommended ? ATTACK_COLORS.primary : '#333',
                      color: '#fff',
                      border: suggestion.recommended ? 'none' : `1px solid ${ATTACK_COLORS.primary}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '14px',
                    }}
                  >
                    <span>
                      {suggestion.icon} {suggestion.title}
                    </span>
                    {suggestion.recommended && (
                      <span style={{
                        backgroundColor: '#4caf50',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                      }}>
                        Recommended
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Console Live */}
          <div style={{
            margin: '8px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#1e1e1e',
            borderRadius: '12px',
            border: `1px solid ${ATTACK_COLORS.primary}30`,
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: `1px solid ${ATTACK_COLORS.primary}30`,
            }}>
              <h4 style={{ color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                🖥️ Console Live
              </h4>
              <button
                onClick={() => setTerminalExpanded(!terminalExpanded)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: ATTACK_COLORS.primary,
                  cursor: 'pointer',
                  fontSize: '18px',
                }}
              >
                {terminalExpanded ? '−' : '+'}
              </button>
            </div>

            {terminalExpanded && (
              <div
                ref={terminalRef}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#0d1117',
                  color: '#c9d1d9',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  overflowY: 'auto',
                }}
              >
                {terminalOutput.map((line, idx) => (
                  <div key={idx} style={{ marginBottom: '4px' }}>
                    <span style={{
                      color: line.type === 'error' ? '#f85149'
                        : line.type === 'success' ? ATTACK_COLORS.success
                        : line.type === 'warning' ? '#d29922'
                        : ATTACK_COLORS.primary,
                    }}>
                      [{line.timestamp}] {line.message}
                    </span>
                  </div>
                ))}
                {terminalOutput.length === 0 && (
                  <span style={{ color: '#8b949e' }}>
                    En attente de commandes...
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Wizard Dialog */}
      {renderWizard()}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default VisualAttackInterface;
